import { useModals } from 'almanac-ui/Modal'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { RegenerateDesignModal } from 'components/RegenerateDesignModal/RegenerateDesignModal'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ConfirmReplaceDesignModal } from '../EditPostMenu/ConfirmReplaceDesignModal'
import { useReplaceFrameContent } from 'hooks/queries/designs/useReplaceFrameContent'
import { usePollDesignReplacement } from '../EditPostMenu/usePollDesignReplacement'
import { useCurrentSlide } from '../CurrentSlideContext'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Upload5 } from 'blaze-ui/icons/20/Upload5'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { isVideoChannel } from '../utils'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ImageMenuSelectedImage, Provider } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import {
  ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useState } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './Sidebar.module.scss'
import { ArrowRotateRight } from 'blaze-ui/icons/20'

interface RedesignSectionProps {
  almanacFile: AlmanacFileShowItem
  isInsertingMedia?: boolean
  onLoadingChange?: (isLoading: boolean) => void
}

export const RedesignSection = observer(
  ({ almanacFile, isInsertingMedia = false, onLoadingChange }: RedesignSectionProps) => {
    const [selectedMedia, setSelectedMedia] = useState<ImageMenuSelectedImage>()
    const { generationTimestampStore } = useRootStore()
    const { currentSlideIndex } = useCurrentSlide()
    const { openModal, closeStack } = useModals()

    const isVideo = almanacFile?.channel ? isVideoChannel(almanacFile.channel as DesignChannel) : false
    const replaceFrameBatchId = generationTimestampStore.getReplaceFrameBatchId(almanacFile.key)
    const isReplacingFrame = !!replaceFrameBatchId
    const isGenerating = almanacFile.is_generating || isReplacingFrame
    const isBusy = isGenerating || isInsertingMedia

    const regenerateLabel = isVideo ? 'Regenerate Video' : 'Regenerate Design'
    const regenerateSubtitle = isVideo ? 'Blaze will generate new video' : 'Blaze will generate new design'
    const replaceLabel = isVideo ? 'Replace with Video' : 'Replace with Media'
    const replaceSubtitle = isVideo ? 'Swap video with your own' : 'Swap design with your own'
    const replaceTitle = isVideo
      ? 'Replace this design with your own video'
      : 'Replace this design with your own image or video'
    const accept = isVideo ? ACCEPTED_VIDEO_MIME_TYPES : ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES_NO_SVG
    const hiddenProviders: Provider[] = isVideo
      ? ['gifs', 'blazeRecommendations', 'stockPhoto', 'hardcodedAi']
      : ['gifs', 'blazeRecommendations']

    const { mutate: replaceFrameContent } = useReplaceFrameContent(
      { designId: almanacFile.key },
      {
        onMutate: () => onLoadingChange?.(true),
        onSuccess: (data) => {
          generationTimestampStore.startGeneration(
            almanacFile.key,
            'default',
            'Replacing design...',
            selectedMedia?.thumbnail_url || selectedMedia?.url,
            data.batch_id,
          )
        },
        onError: () => {
          onLoadingChange?.(false)
          generationTimestampStore.completeGeneration(almanacFile.key)
          showToastMessage('Failed to replace design', 'error')
        },
      },
    )

    usePollDesignReplacement({
      replaceFrameBatchId,
      designKey: almanacFile.key,
      onComplete: () => {
        generationTimestampStore.completeGeneration(almanacFile.key)
        onLoadingChange?.(false)
      },
      scheduledPostId: almanacFile?.scheduled_post?.id,
    })

    const handleRegenerate = () => {
      openModal(RegenerateDesignModal, { almanacFile }, { newStack: true })
    }

    const handleReplace = () => {
      openModal(
        ImageMenuModal,
        {
          title: replaceTitle,
          hiddenProviders,
          accept,
          uploadMedia: true,
          maxCount: 1,
          closeOnUse: false,
          onUse: (images: ImageMenuSelectedImage[]) => {
            const media = images[0]
            setSelectedMedia(media)
            openModal(ConfirmReplaceDesignModal, {
              title: replaceTitle,
              mediaUrl: media.url,
              mediaMimeType: media.mime_type || '',
              onConfirm: () => {
                if (!media?.id) return
                replaceFrameContent({
                  frames: [
                    {
                      frame_position: currentSlideIndex + 1,
                      media_file_key: media.id,
                    },
                  ],
                })
                closeStack()
              },
            })
          },
        },
        { newStack: true },
      )
    }

    return (
      <>
        <div className={Styles.sectionLabel}>
          <Text variant="placeholder" color="var(--dark-40)">
            Redesign
          </Text>
        </div>
        <div>
          <button className={Styles.redesignButton} disabled={isBusy} onClick={handleRegenerate}>
            <ArrowRotateRight size={15} />
            <div className={Styles.redesignButtonText}>
              <Text variant="secondary" color="var(--dark-90)">
                {regenerateLabel}
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {regenerateSubtitle}
              </Text>
            </div>
          </button>

          <button className={Styles.redesignButton} disabled={isBusy} onClick={handleReplace}>
            <Upload5 size={15} />
            <div className={Styles.redesignButtonText}>
              <Text variant="secondary" color="var(--dark-90)">
                {replaceLabel}
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {replaceSubtitle}
              </Text>
            </div>
          </button>
        </div>
      </>
    )
  },
)
